import { useEffect, useState } from 'react';
import { Contract, User } from 'type/type';
import { useForm } from 'react-hook-form';
import { createUser, encryptSHA256, getAdminContractByNo, getContractStatusValid, getIdDuplicateCheck, getUserContractByContractNo, maxLengthCheck } from 'api/api';
import hubLayout from 'styles/css/hubLayout.module.css';
import { useNavigate } from 'react-router-dom';
import { useModal } from 'components/common/ModalLayout';
import Loading from 'components/common/Loading';
import { contractStatusLabel } from 'pages/utils/commonUtils';

function UserRegister() {
  const navigate = useNavigate();
  const { showModal } = useModal();
  const [loading, setLoading] = useState(false);

  // useForm
  const { register, formState: { errors, isValid } } = useForm<User>({
    mode: 'all',
  });
  const [userId, setUserId] = useState<string>('');
  const [contract, setContract] = useState<Contract>(Object);
  const [user, setUser] = useState<User>({
    idx: 0,
    id: '',
    password: '',
    name: '',
    post_code: '',
    address: '',
    address_detail: '',
    mobile: '',
    os_type: '', //A or I
    os_ver: '', //OS 버전
    app_ver: '', //앱 버전
    phone_model: '', //핸드폰 모델
    email: '', //이메일
    push_token: '', //PUSH 전송 토큰
    ci: '', //본인확인기관이 주민등록번호를 암호화하여 생성한 고유식별정보
    dormant_stat: '0', //발송유무
    contract_no: '', //고객 생성시 필요
  });
  const [searchValue, setSearchValue] = useState<string>('');

  const getContract = async () => {
    setLoading(true);
    try {
      if (!searchValue) {
        return showModal('alert', '계약번호를 입력해주세요.');
      }
  
      const contractRes = await getAdminContractByNo(searchValue);
      if (contractRes.code !== 200) {
        setContract({} as Contract);
        setUser({ dormant_stat: '0'} as User);
        return showModal('alert', contractRes.message);
      }
  
      const contractData = contractRes.data;
      setContract(contractData);
      setUser((prevData) => ({
        ...prevData,
        name: contractData.name,
        mobile: contractData.mobile,
        email: contractData.email,
        post_code: contractData.post_code,
        address: contractData.address,
        address_detail: contractData.address_detail,
      }));
  
      if (!getContractStatusValid(contractData.contract_status)) {
        return showModal('alert', '승인된 계약번호가 아닙니다.\n승인 후 계정 생성이 가능합니다.');
      }
  
      const usersResponse = await getUserContractByContractNo(contractData.contract_no);
      if (usersResponse.code === 200 && usersResponse.data) {
        const usersData = usersResponse.data;
        setUserId(usersData.user_id);
        return showModal('alert', `이미 계정 생성이 완료된 계약번호입니다.\nID : ${usersData.user_id}`);
      }
  
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  function contractNoHandleChange(event: any) {
    maxLengthCheck(event);

    const input = event.target.value;
    // 숫자만 추출
    const numericValue = input.replace(/[^0-9]/g, "");
    
    setSearchValue(numericValue);
    setUser((prevData) => ({
      ...prevData,
      'contract_no': numericValue,
    }));
  }

  const handleChange = (event: any) => {
    const { value } = event.target;
    const lowercaseValue = value.toLowerCase();
    const encPassword = encryptSHA256(lowercaseValue);
    setUser((prevData) => ({
      ...prevData,
      'id': lowercaseValue,
      'password': encPassword,
    }));
  }

  const duplicateCheck = async () => {
    setLoading(true);
    try {
      if (!isValid || !user.id) {
        return showModal('alert', 'ID를 정확히 입력해주세요.');
      }
      const response = await getIdDuplicateCheck(user.id);
      if (response.code === 200) {
        showModal('confirm', `'${user.id}'는 사용가능한 아이디입니다. 아이디를 생성하시겠습니까?`,  {confirm: false, cancel: false},
          () => {
            _createContract();
          },
          () => {
            console.log("취소");
          },
        );
      } else {
        showModal('alert', `이미 사용중인 아이디입니다. 다시 입력해주세요.`);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  const _createContract = async () => {
    setLoading(true);
    try {
      const response = await createUser(user);
      if (response.code === 200) {
        showModal('confirm','아이디를 생성하였습니다. 이어서 장비를 등록해 주세요.',
          {confirm: false, cancel: false},
          () => {
            navigate(`/agent/recorder/list/${user.id}`);
          },
          () => {
            navigate(-1);
          }
        );
      } else {
        showModal('alert', response.message);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const handleResize = () => {
      window.scrollTo(0, document.body.scrollHeight);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  return (
    <>
      {loading ? <Loading />
      :
        <div className={`${hubLayout.cnt_box}`}>
          <div className={`${hubLayout.acc__cnt}`}>
            <div className={`${hubLayout.sec_title}`}>
              <h2>고객 등록</h2>
              <span onClick={() => navigate(-1)}>X</span>
            </div>
            <h2 className={`${hubLayout.cnt_title} ${hubLayout.tx_blue}`}>계약 조회</h2>
            <div className={`${hubLayout.acc_item_info}`}>
              <dl className={`${hubLayout.margin_0}`}>
                <dt>계약번호</dt>
                <dd>
                  <div className={`${hubLayout.display_flex}`}>
                    <input type="text" name="contract_no" value={searchValue} maxLength={9} onChange={contractNoHandleChange} />
                    <button type="button" className={`${hubLayout.btn} ${hubLayout.medium} ${hubLayout.blue} ${hubLayout.width_100}`} onClick={getContract}>조회</button>
                  </div>
                </dd>
              </dl>
            </div>
            {contract.idx > 0 &&
              <>
                <h2 className={`${hubLayout.cnt_title} ${hubLayout.tx_blue}`}></h2>
                <div className={`${hubLayout.acc_item_info}`}>
                  <dl className={`${hubLayout.margin_0}`}>
                    <dt>상호명</dt>
                    <dd>
                      <input type="text" value={contract.company_name} readOnly />
                    </dd>
                  </dl>
                  <dl className={`${hubLayout.margin_0}`}>
                    <dt>대표자명</dt>
                    <dd>
                      <input type="text" value={contract.name} readOnly />
                    </dd>
                  </dl>
                  <dl className={`${hubLayout.margin_0}`}>
                    <dt>사업장주소</dt>
                    <dd>
                      <input type="text" value={`${contract.company_address} ${contract.company_address_detail}`} readOnly />
                    </dd>
                  </dl>
                  <dl className={`${hubLayout.margin_0}`}>
                    <dt>계약상품</dt>
                    <dd>
                      <input type="text" value={contract.contract_product} readOnly />
                    </dd>
                  </dl>
                  <dl className={`${hubLayout.margin_0}`}>
                    <dt>계약상태</dt>
                    <dd>
                      <input type="text" value={contractStatusLabel.find(item => item.code === contract.contract_status)?.name || ""} readOnly />
                    </dd>
                  </dl>
                </div>
                {getContractStatusValid(contract.contract_status) && !userId &&
                  <>
                    <h2 className={`${hubLayout.cnt_title} ${hubLayout.tx_blue}`}>계정 생성</h2>
                    <div className={`${hubLayout.acc_item_info}`}>
                      <dl className={`${hubLayout.margin_0}`}>
                        <dt>ID</dt>
                        <dd>
                          <div className={`${hubLayout.display_flex}`}>
                            <input type="text" value={user.id} maxLength={20} {...register("id", {
                              required: true,
                              pattern: /^[a-z][0-9a-z]{3,19}$/g,
                              onChange(e) {
                                maxLengthCheck(e);
                                handleChange(e);
                              },
                            })}/>
                            <button type="button" className={`${hubLayout.btn} ${hubLayout.medium} ${hubLayout.blue} ${hubLayout.width_100}`} onClick={duplicateCheck}>조회</button>
                          </div>
                        </dd>
                      </dl>
                      <p className={`${errors.id && hubLayout.tx_red}`}>* 아이디는 영문 소문자로 시작하며, 4~20자의 영문 소문자와 숫자로만 입력 가능합니다.</p>
                    </div>
                  </>
                }
                <div className={`${hubLayout.btn_group} ${hubLayout.fixed_bottom_title} ${hubLayout.back_white}`}>
                  <button type="button" className={`${hubLayout.btn} ${hubLayout.large} ${hubLayout.pink}`} onClick={() => navigate(-1)}>목록</button>
                </div>
              </>
            }
          </div>
        </div>
      }
    </>
  );
}

export default UserRegister;